import { LayingPatternViewer } from 'CommonScripts/layingPatternViewer';
import { MixedColour, ProductTexture } from 'CommonData/poco/index';
import { LoadingHelper } from 'CommonScripts/loadingHelper';
import { updateProductName } from './textureSelect';

const colourMixingBoxEl: Element = document.getElementsByClassName('js-colour-mixing-box')[0];
const colourMixingBoxCloseEl: Element = document.getElementsByClassName('js-close-mix-box')[0];
const baseSelectedColourEl: Element = document.getElementsByClassName('js-selected-colour-list-item')[0];
const baseColourOptionEl: Element = document.getElementsByClassName('js-colour option-list-item')[0];
const selectedColourListEl: Element = document.getElementsByClassName('js-selected-colour-list')[0];
const colourOptionsListEl: Element = document.getElementsByClassName('js-colour option-list')[0];
const selectedColourInputEls: HTMLCollectionOf<HTMLInputElement> = document.getElementsByClassName('js-selected-colour-input') as HTMLCollectionOf<HTMLInputElement>;
const selectedColourRelativePercentageEls: HTMLCollectionOf<HTMLInputElement> = document.getElementsByClassName('js-selected-colour-relative-percentage') as HTMLCollectionOf<HTMLInputElement>;
const applyButtonEl: HTMLButtonElement = document.getElementById('apply-colour-mixing') as HTMLButtonElement;
const combinedRangeSliderEl: Element = document.getElementsByClassName('colour-mixing__combined-slider')[0];
const baseColourRangeSliderEl: Element = document.getElementsByClassName('js-colour-range')[0];

colourMixingBoxCloseEl.addEventListener('click', () => {
    ColourMixing.closeBox();
});

applyButtonEl.addEventListener('click', () => {
    ColourMixing.applyColourMixing();
});

export class ColourMixing {
    public static baseTextureKey: string;
    public static selectedJoint: string;
    private static readonly stepCount: number = 5;

    public static setBaseSelectedColour({ productTextures, colourEl, textureKey } : { productTextures: Array<ProductTexture>, colourEl?: Element, textureKey?: string }): Promise<void> {
        return new Promise<void>(function(resolve, reject) {
            if (!textureKey) {
                if (!colourEl) {
                    colourEl = document.getElementsByClassName('js-product selected')[0];
                }
    
                textureKey = colourEl.getAttribute('data-key');
                if (!textureKey) {
                    console.error('Texture key not found in: setBaseSelectedColour');
                    reject();
                }
            }
        
            const matchingProductTexture = productTextures.find((pt: ProductTexture) => pt.textureKey === textureKey);
            if (!matchingProductTexture) {
                console.error('Product Texture not found in: setBaseSelectedColour');
                reject();
            }

            if (textureKey != ColourMixing.baseTextureKey) {
                selectedColourListEl.innerHTML = '';
                combinedRangeSliderEl.innerHTML = '';
                ColourMixing.createSelectedColourEl(matchingProductTexture, true);
                ColourMixing.createSelectedColourRangeInputEl(matchingProductTexture);
                ColourMixing.setColourOptions(productTextures, matchingProductTexture);
                ColourMixing.baseTextureKey = textureKey;
            }

            resolve();
        });
    }

    public static addSelectedColour({ productTextures, colourEl, textureKey } : { productTextures: Array<ProductTexture>, colourEl?: Element, textureKey?: string }): void {
        if (!colourOptionsListEl) {
            console.error('Colour options list element could not be found: ColourMixing.addSelectedColour')
            return;
        }
        
        if (!textureKey && !colourEl) {
            throw 'No matching data provided to: addSelectedColour';
        }

        if (colourEl && !textureKey) {
            textureKey = colourEl.getAttribute('data-key');
            if (!textureKey) {
                throw 'Texture key not found in: setBaseSelectedColour';
            }
        }

        if (document.getElementsByClassName('js-selected-colour-list-item')?.length >= 4) {
            return;
        }

        [].slice.call(colourOptionsListEl.getElementsByClassName('js-colour')).find((el: HTMLElement) => el.getAttribute('data-key') == textureKey).classList.add('selected');

        const matchingProductTexture = productTextures.find((pt: ProductTexture) => pt.textureKey === textureKey);
        if (!matchingProductTexture) {
            throw 'Product Texture not found in: addSelectedColour';
        }

        this.createSelectedColourEl(matchingProductTexture);
        this.createSelectedColourRangeInputEl(matchingProductTexture);
    }

    public static removeSelectedColour(textureKey: string): void {
        const selectedColourEls: Array<HTMLElement> = [].slice.call(selectedColourListEl.getElementsByClassName('js-selected-colour-list-item'));
        const selectedColourRangeEls: Array<HTMLInputElement> = [].slice.call(combinedRangeSliderEl.getElementsByClassName('js-colour-range'));
        const matchingSelectedColourEl: HTMLElement = selectedColourEls.find((el: HTMLElement) => el.getAttribute('data-key') == textureKey);
        const matchingSelectedColourRangeEl: HTMLInputElement = selectedColourRangeEls.find((el: HTMLElement) => el.getAttribute('data-key') == textureKey);

        if (matchingSelectedColourEl) {
            matchingSelectedColourEl.remove();
        }

        if (matchingSelectedColourRangeEl) {
            matchingSelectedColourRangeEl.remove();
        }

        [].slice.call(colourOptionsListEl.getElementsByClassName('js-colour')).find((el: HTMLElement) => el.getAttribute('data-key') == textureKey).classList.remove('selected');

        this.recalculateColourPercentages();
    }

    public static async applyColourMixing(): Promise<void> {
        let newTextureKey: string = '';
        for (let inputPercentageEl of selectedColourRelativePercentageEls) {
            const valueStr: string = inputPercentageEl.getAttribute('data-value');
            if (!valueStr) {
                return;
            }

            const value: number = parseInt(valueStr);
            if (!value || isNaN(value)) {
                return;
            }

            let textureKey: string = inputPercentageEl.parentElement.getAttribute('data-key');
            textureKey = textureKey.includes('|') ? textureKey.split('|')[0] : textureKey;

            newTextureKey += `${textureKey}|${value}|`;
        }

        if (newTextureKey.length > 0) {
            newTextureKey = newTextureKey.substring(0, newTextureKey.length - 1);
        }

        try {
            LoadingHelper.start();
            return await LayingPatternViewer.updateProductTexture({ textureKey: newTextureKey });
        } finally {
            updateProductName(true);
            LoadingHelper.stop();
        }
    }

    public static recalculateColourPercentages(isInitialCalculation: boolean = true): void {
        if (isInitialCalculation) {
            for (const inputEl of selectedColourInputEls) {
                inputEl.value = 100 / selectedColourInputEls.length + '';
            }
    
            this.setColourRangeInputElWidth();
        }

        let consumedWidthPercentage = 0;

        for (let i = selectedColourRelativePercentageEls.length - 1; i >= 0; i--) {
            const valueEl = selectedColourRelativePercentageEls[i];

            if (!valueEl) {
                return;
            }

            const matchingRangeEl: HTMLInputElement = [].slice.call(combinedRangeSliderEl.children).find((rangeEl: HTMLInputElement) => rangeEl.getAttribute('data-key') == valueEl.getAttribute('data-key'));
            const widthRatio: number = matchingRangeEl.offsetWidth / (matchingRangeEl.parentElement.offsetWidth);
            const widthPercentage: number = Math.round((widthRatio * 100) / this.stepCount) * this.stepCount;
            const percentageWidth: number = i == 0 ? 100 - consumedWidthPercentage : widthPercentage - consumedWidthPercentage;
            
            valueEl.innerHTML = `${percentageWidth}%`;
            valueEl.setAttribute('data-value', String(percentageWidth));
            consumedWidthPercentage += percentageWidth;
        }
    }

    public static closeBox(): void {
        colourMixingBoxEl.classList.add('hidden');
    }

    public static openBox(): void {
        colourMixingBoxEl.classList.remove('hidden');
    }

    public static setSelectedJointUrl(jointUrl: string, isInitialSet: boolean = false): void {
        if (isInitialSet) {
            this.selectedJoint = jointUrl;
        }

        combinedRangeSliderEl.setAttribute('style', `--joint-image: url(${this.selectedJoint});`)
    }

    private static setColourOptions(productTextures: Array<ProductTexture> , productTexture: ProductTexture): void {
        colourOptionsListEl.innerHTML = '';

        const sortedMixedColours: Array<MixedColour> = productTexture.mixedColours?.sort((a: MixedColour, b: MixedColour) => a.name.includes(productTexture.productName) ? -1 : 1);

        sortedMixedColours.forEach((mx: MixedColour) => {
            const optionEl: HTMLElement = baseColourOptionEl.cloneNode(true) as HTMLElement;
            const img: HTMLElement = optionEl.getElementsByTagName('img')[0];
            const p: HTMLParagraphElement = optionEl.getElementsByTagName('p')[0];

            optionEl.setAttribute('data-key', mx.textureKey);
            img.setAttribute('src', mx.swatchImageUrl);
            p.innerHTML = mx.name;

            optionEl.addEventListener('click', () => {
                if (!optionEl.classList.contains('selected')) {
                    this.addSelectedColour({ productTextures, textureKey: mx.textureKey });
                    // optionEl.classList.add('selected');
                } else {
                    this.removeSelectedColour(mx.textureKey);
                    // optionEl.classList.remove('selected');
                }

                this.recalculateColourPercentages();
            });

            colourOptionsListEl.appendChild(optionEl);
        });

        if (sortedMixedColours.length >= 5) {
            colourOptionsListEl.classList.add('scrollable');
        } else {
            colourOptionsListEl.classList.remove('scrollable');
        }
    }

    private static createSelectedColourEl(productTexture: ProductTexture, isBase: boolean = false): void {
        const el: HTMLElement = baseSelectedColourEl.cloneNode(true) as HTMLElement;
        const closeDiv: HTMLElement = el.getElementsByTagName('div')[0];
        const img: HTMLElement = el.getElementsByTagName('img')[0];
        const input: HTMLElement = el.getElementsByTagName('input')[0];
        const percentageSpan: HTMLElement = el.getElementsByTagName('span')[0];
        const nameSpan: HTMLElement = el.getElementsByTagName('span')[1];
    
        el.setAttribute('data-key', productTexture.textureKey);
        img.setAttribute('src', productTexture.materialSwatchImageUrl);
        input.setAttribute('id', productTexture.textureKey);
        percentageSpan.setAttribute('data-key', productTexture.textureKey);
        nameSpan.innerHTML = productTexture.name;
        
        closeDiv.addEventListener('click', () => {
            this.removeSelectedColour(productTexture.textureKey);
        });

        if (isBase) {
            closeDiv.classList.add('hidden');
        }

        selectedColourListEl.appendChild(el);
    }

    private static createSelectedColourRangeInputEl(productTexture: ProductTexture): void {
        const swatchUrl: string = productTexture.hasMaterialTextureKey && productTexture.hasDiffuseMaterialSwatchImageUrl ? productTexture.materialSwatchImageUrl : productTexture.skuSwatchImageUrl;
        const el: HTMLInputElement = baseColourRangeSliderEl.cloneNode(true) as HTMLInputElement;
        el.setAttribute('data-key', productTexture.textureKey);
        el.setAttribute('style', `--texture-image: url(${swatchUrl});`);
        this.setSelectedJointUrl(this.selectedJoint);

        el.addEventListener('input', () => {
            this.setColourRangeInputElWidth(el);
        });

        combinedRangeSliderEl.appendChild(el);
    }

    private static setColourRangeInputElWidth(inputEl: HTMLInputElement = combinedRangeSliderEl.firstElementChild as HTMLInputElement): void {
        const reduceNextInputEl = (el: HTMLInputElement) => {
            const nextInputEl: HTMLInputElement = el.nextElementSibling as HTMLInputElement;
            if (el && nextInputEl) {
                const widthRatio: number = el.offsetWidth / el.parentElement.offsetWidth;
                const widthPercentage: number = widthRatio * 100
                const finalWidthPercentage = widthPercentage - (parseInt(el.value) * widthRatio)
                nextInputEl.style.width = `${finalWidthPercentage}%`;

                if (finalWidthPercentage <= 0) {
                    nextInputEl.classList.add('hidden');
                } else {
                    nextInputEl.classList.remove('hidden');
                }

                reduceNextInputEl(nextInputEl);
            }
        };

        const nextInputEl: HTMLInputElement = inputEl.nextElementSibling as HTMLInputElement;
        const previousInputEl: HTMLInputElement = inputEl.previousElementSibling as HTMLInputElement;

        if (!nextInputEl && !previousInputEl) {
            return;
        }

        if (nextInputEl) {
            reduceNextInputEl(inputEl);
            this.recalculateColourPercentages(false);
        }
    }
}

ColourMixing.recalculateColourPercentages();